import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, ExternalLink, LogIn, LogOut, Camera, Plus, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/constants";
import { EventDialog } from "@/components/EventDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (path) => {
  const t = localStorage.getItem("sc_token");
  return t ? `${BACKEND}/api/visits/photo/${path}?auth=${t}` : "";
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");
const fmtDur = (m) => (m == null ? "" : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

function MapLink({ lat, lng, testid }) {
  if (lat == null) return null;
  return (
    <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer"
       className="text-brand text-xs inline-flex items-center gap-0.5 hover:underline" data-testid={testid}>
      <MapPin className="h-3 w-3" /> Map <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function OngoingBanner({ event, onCheckout, onRefresh }) {
  const [, tick] = useState(0);
  const [note, setNote] = useState("");
  const [addingPhoto, setAddingPhoto] = useState(false);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 30000); return () => clearInterval(t); }, []);
  const mins = Math.max(0, Math.round((Date.now() - new Date(event.check_in_at).getTime()) / 60000));

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/visits/${event.id}/note`, { text: note.trim() });
      setNote(""); onRefresh?.();
      toast.success("Note added");
    } catch (e) { toast.error(apiError(e)); }
  };

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setAddingPhoto(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f, f.name || "photo.jpg");
        await api.post(`/visits/${event.id}/photo`, fd);
      }
      onRefresh?.();
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} added`);
    } catch (err) { toast.error(apiError(err)); } finally { setAddingPhoto(false); }
  };

  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50/60 p-3 mb-3" data-testid="ongoing-event-banner">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="flex items-center gap-2 text-sm font-medium text-emerald-800">
          <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>
          Checked in at {fmtTime(event.check_in_at)} · {fmtDur(mins)} on site
        </span>
        <Button size="sm" onClick={onCheckout} className="h-8 gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="check-out-btn">
          <LogOut className="h-3.5 w-3.5" /> Check Out
        </Button>
      </div>
      <div className="flex gap-1.5 mt-2.5">
        <Input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()}
               placeholder="Add a note while on site…" className="h-8 text-sm bg-white" data-testid="event-add-note-input" />
        <Button size="sm" variant="outline" onClick={addNote} className="h-8" data-testid="event-add-note-btn"><Plus className="h-3.5 w-3.5" /></Button>
        <label className={`h-8 w-9 rounded-md border border-slate-300 bg-white flex items-center justify-center cursor-pointer hover:border-brand text-slate-500 hover:text-brand ${addingPhoto ? "opacity-50 pointer-events-none" : ""}`} data-testid="event-add-photo-label">
          <Camera className="h-4 w-4" />
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addPhoto} data-testid="event-add-photo-input" />
        </label>
      </div>
    </div>
  );
}

export function EventsCard({ entityType, entityId, company, canEdit = true, onSaved }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // "checkin" | "checkout"
  const [lightbox, setLightbox] = useState(null);

  const { data: visits = [] } = useQuery({
    queryKey: ["visits", entityType, entityId],
    queryFn: async () => (await api.get("/visits", { params: { entity_type: entityType, entity_id: entityId } })).data,
  });
  const ongoing = visits.find((v) => v.status === "checked_in" && v.user_id === user?.id);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["visits", entityType, entityId] });
    qc.invalidateQueries({ queryKey: ["active-event"] });
    onSaved?.();
  };

  return (
    <Card className="p-5" data-testid="events-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-sm text-slate-800 flex items-center gap-1.5"><MapPin className="h-4 w-4 text-brand" /> Events</h3>
        {canEdit && !ongoing && (
          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setDialog("checkin")} data-testid="check-in-btn">
            <LogIn className="h-3.5 w-3.5" /> Check In
          </Button>
        )}
      </div>

      {ongoing && <OngoingBanner event={ongoing} onCheckout={() => setDialog("checkout")} onRefresh={refresh} />}

      {visits.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">No events yet — check in when you arrive on site.</p>
      ) : (
        <div className="space-y-3">
          {visits.map((v) => (
            <div key={v.id} className="border border-slate-200 rounded-md p-3" data-testid={`visit-${v.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-medium text-slate-800">{formatDate(v.visit_date)} · {v.user_name}</p>
                {v.status === "checked_in" && <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">Ongoing</span>}
                {v.no_checkout && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full" title="Auto-closed after 8h without check-out"><AlertTriangle className="h-3 w-3" /> No check-out</span>}
              </div>
              {v.check_in_at ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1"><LogIn className="h-3 w-3 text-emerald-600" /> In {fmtTime(v.check_in_at)} <MapLink lat={v.latitude} lng={v.longitude} testid={`visit-in-map-${v.id}`} /></span>
                  {v.check_out_at && (
                    <span className="inline-flex items-center gap-1"><LogOut className="h-3 w-3 text-slate-500" /> Out {fmtTime(v.check_out_at)} <MapLink lat={v.check_out_lat} lng={v.check_out_lng} testid={`visit-out-map-${v.id}`} /></span>
                  )}
                  {v.duration_minutes != null && <span className="inline-flex items-center gap-1 text-slate-500"><Clock className="h-3 w-3" /> {fmtDur(v.duration_minutes)}</span>}
                </div>
              ) : (
                <div className="mt-1">
                  {v.latitude != null ? <MapLink lat={v.latitude} lng={v.longitude} testid={`visit-map-${v.id}`} /> : <span className="text-xs text-slate-400">No GPS</span>}
                </div>
              )}
              {v.address && <p className="text-xs text-slate-500 mt-0.5">{v.address}</p>}
              {v.notes && <p className="text-sm text-slate-600 mt-1.5">{v.notes}</p>}
              {(v.extra_notes || []).map((n, i) => (
                <p key={i} className="text-sm text-slate-600 mt-1"><span className="text-[10px] text-slate-400 mr-1">{fmtTime(n.at)}</span>{n.text}</p>
              ))}
              {v.checkout_notes && <p className="text-sm text-slate-600 mt-1"><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Check-out</span>{v.checkout_notes}</p>}
              {v.photos?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {v.photos.map((p) => (
                    <button key={p.id} onClick={() => setLightbox(photoUrl(p.storage_path))} className="h-14 w-14 rounded-md overflow-hidden border hover:ring-2 hover:ring-brand" data-testid={`visit-thumb-${p.id}`}>
                      <img src={photoUrl(p.storage_path)} alt="site" className="h-full w-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <EventDialog open={dialog !== null} onOpenChange={(v) => !v && setDialog(null)} mode={dialog || "checkin"}
        entityType={entityType} entityId={entityId} company={company} event={ongoing} onSaved={refresh} />

      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-2xl p-2" data-testid="photo-lightbox">
          {lightbox && <img src={lightbox} alt="site visit" className="w-full rounded-md" />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
